import type { ConversationResponse } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/query";
import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useLauncherContext } from "../../../../lib/client/contexts/LauncherContext";
import { createStore, type StoreApi, useStore } from "zustand";
import type { Message } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/entities";
import { useSessionContext } from "./SessionContext";
import { socket } from "../socket";
import type { ServerToClientEvents } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/event";
import { captureSentryException } from "@/lib/client/utils/captureSentryException";
import useRecipient from "../../../(tabs)/messages/[id]/lib/client/hooks/useRecipient";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { isChatbotContent } from "../../../(tabs)/messages/[id]/types";
import { useDevice, useLocale } from "./DeviceContext";

type InboundMessage = Message & {
  type: "inbound";
};

export type ConversationsProps = {
  conversations: Record<string, ConversationResponse>;
};

export type ConversationsState = ConversationsProps & {
  addInboundMessage: (conversationId: string, message: InboundMessage) => void;
  createConversation(chatflowId: number): Promise<ConversationResponse>;
  setReadMessage: (conversationId: string, messageId: string, time: string) => void;
  setConversationCompleted: (conversationId: string) => void;
  getLatestReadMessage: (conversationId: string) => Message | null;
  getUnreadCount: (...ids: string[]) => number;
  getUnreadCountExcept: (...ids: string[]) => number;
};

type PrivateConversationsState = ConversationsState & {
  addConversations: (conversations: Record<string, ConversationResponse>) => void;
  addSocketMessage: (conversationId: string, message: Message) => void;
  setTicket: (conversationId: string, ticketId: number) => void;
  endChatflow: (conversationId: string) => void;
};

function unread(
  conversations: ConversationsProps["conversations"],
  filter: (id: string) => boolean,
) {
  const filteredConversations = Object.entries(conversations)
    .filter(([id]) => filter(id))
    .map(([, c]) => c);
  const filteredCounts = filteredConversations.map(
    (c) => Object.keys(c.messages.unreadMessages || {}).length,
  );
  const count = filteredCounts.reduce((acc, curr) => acc + curr, 0);
  return count;
}

function addMessageToConversation(
  state: ConversationsProps,
  conversationId: string,
  message: Message,
): ConversationsProps {
  if (!(conversationId in state.conversations)) {
    return state;
  }
  const conversations = state.conversations[conversationId];
  let unread = message.type === "outbound" && message.content.type !== "replyBoxInput";

  const existingItems = conversations.messages.items;
  let items: Message[];

  const existingIndex = existingItems.findIndex(
    (i) =>
      i.id === message.id ||
      // if both are chatbot content, compare the type, and check if content.id is the same
      (isChatbotContent(i.content) &&
        isChatbotContent(message.content) &&
        i.content.type === message.content.type &&
        "id" in i.content &&
        "id" in message.content &&
        i.content.id === message.content.id),
  );

  if (existingIndex >= 0) {
    unread = false; // Let the existing unread status apply
    items = [
      ...existingItems.slice(0, existingIndex),
      message,
      ...existingItems.slice(existingIndex + 1),
    ];
  } else {
    items = [message, ...existingItems];
  }

  let unreadMessages = conversations.messages.unreadMessages;
  if (unread) {
    unreadMessages = {
      ...unreadMessages,
      [message.id]: message.author.sent_at?.length
        ? message.author.sent_at
        : new Date().toUTCString(),
    };
  }

  return {
    ...state,
    conversations: {
      ...state.conversations,
      [conversationId]: {
        ...state.conversations[conversationId],
        messages: { ...state.conversations[conversationId].messages, items, unreadMessages },
      },
    },
  };
}

function isNeverRead(message: Message) {
  // These messages are always ignored by the UI - they are never considered "read"
  return message.content.type === "replyBoxInput";
}

function isUnread(message: Message, unread: Record<string, string>) {
  return message.id in unread;
}

function getLatestReadMessage(conversation: ConversationResponse | undefined): Message | null {
  const unread = conversation?.messages?.unreadMessages || {};
  return (
    conversation?.messages?.items?.find((m) => !isNeverRead(m) && !isUnread(m, unread)) || null
  );
}

const DEFAULT_PROPS: ConversationsProps = {
  conversations: {},
};

export const useConversationsStore = (tenant: string, initProps?: Partial<ConversationsProps>) => {
  const { api } = useLauncherContext(); // Will change on different tenant or different messenger
  const { session } = useSessionContext();
  const { device_id, type: device_type } = useDevice();
  const { locale } = useLocale();

  const conversationsStore = useMemo(() => {
    return createStore<PrivateConversationsState>()((set, get) => ({
      ...DEFAULT_PROPS,
      ...initProps,
      getLatestReadMessage: (conversationId: string) =>
        getLatestReadMessage(get().conversations[conversationId]),
      getUnreadCount: (...ids: string[]) => unread(get().conversations, (i) => ids.includes(i)),
      getUnreadCountExcept: (...ids: string[]) =>
        unread(get().conversations, (i) => !ids.includes(i)),
      addInboundMessage: (conversationId: string, message: InboundMessage) =>
        set((state) => addMessageToConversation(state, conversationId, message)),
      setReadMessage: (conversationId: string, messageId: string, read_at: string) => {
        set((state) => {
          if (!(conversationId in state.conversations)) {
            return state;
          }
          const conversation = state.conversations[conversationId];
          const existingItems = conversation.messages.items;
          const messageIndex = existingItems.findIndex((i) => i.id === messageId);
          if (messageIndex < 0) {
            return state;
          }
          let message = existingItems[messageIndex];
          if (message.recipients) {
            const recipients = message.recipients.map((r) => {
              if (r.type === "user") {
                return { ...r, read_at };
              }
              return r;
            });
            message = { ...message, recipients };
          }
          const items = [
            ...existingItems.slice(0, messageIndex),
            { ...message },
            ...existingItems.slice(messageIndex + 1),
          ];
          const { [messageId]: _, ...unreadMessages } = conversation.messages.unreadMessages || {};
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...state.conversations[conversationId],
                messages: {
                  ...state.conversations[conversationId].messages,
                  unreadMessages,
                  items,
                },
              },
            },
          };
        });
      },
      createConversation: async (chatflowId: number) => {
        if (!session) {
          throw new Error("Cannot create a new conversation without a session");
        }
        const conversation = await api.createConversation({
          recipient: {
            external_id: device_id,
            first_name: session.user?.first_name,
            last_name: session.user?.last_name,
            device: {
              type: device_type,
              device_id: device_id,
            },
            locale,
          },
          created_at: new Date().toISOString(),
          workflow_id: chatflowId,
        });
        if (conversation) {
          set((state) => ({
            ...state,
            conversations: { ...state.conversations, [conversation.id]: conversation },
          }));
        }
        return conversation;
      },
      setConversationCompleted: (conversationId: string) => {
        set((state) => {
          if (!(conversationId in state.conversations)) {
            captureSentryException(
              `[ConversationsContext.setConversationCompleted] conversationId ${conversationId} does not exist in conversations`,
            );
            return state;
          }
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [conversationId]: { ...state.conversations[conversationId], status: "complete" },
            },
          };
        });
      },
      // Private methods
      addConversations: (conversations: Record<string, ConversationResponse>) =>
        set((state) => ({ ...state, conversations: { ...state.conversations, ...conversations } })),
      setTicket: (conversationId: string, ticketId: number) => {
        set((state) => {
          if (!(conversationId in state.conversations)) {
            captureSentryException(
              `[ConversationsContext.setTicket] conversationId ${conversationId} does not exist in conversations`,
            );
            return state;
          }
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [conversationId]: { ...state.conversations[conversationId], ticket_id: ticketId },
            },
          };
        });
      },
      addSocketMessage: (conversationId: string, message: Message) =>
        set((state) => {
          if (!(conversationId in state.conversations)) {
            captureSentryException(
              `[ConversationsContext.addSocketMessage] conversationId ${conversationId} does not exist in conversations`,
            );
            return state;
          }
          return addMessageToConversation(state, conversationId, message);
        }),
      endChatflow: (conversationId: string) =>
        set((state) => {
          if (!(conversationId in state.conversations)) {
            captureSentryException(
              `[ConversationsContext.endChatflow] conversationId ${conversationId} does not exist in conversations`,
            );
            return state;
          }
          const { chatflow_id, ...currentConversation } = state.conversations[conversationId];
          return {
            ...state,
            conversations: {
              ...state.conversations,
              [conversationId]: currentConversation,
            },
          };
        }),
    }));
  }, [api, device_id, device_type, initProps, session, locale]);

  const addConversations = useStore(conversationsStore, (s) => s.addConversations);
  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(async () => {
      if (cancelled) return;
      const conversations: ConversationResponse[] = await api.getConversations();
      if (cancelled || conversations.length === 0) return;
      const c = conversations.reduce<Record<string, ConversationResponse>>((acc, curr) => {
        acc[curr.id] = curr;
        return acc;
      }, {});
      addConversations(c);
    });
    return () => {
      cancelled = true;
    };
  }, [api, addConversations]);

  const endChatflow = useStore(conversationsStore, (s) => s.endChatflow);
  useEffect(() => {
    const onChatflowEnded: ServerToClientEvents["chatflowEnded"] = ({
      data: { conversation_id },
    }) => endChatflow(conversation_id);

    socket.on("chatflowEnded", onChatflowEnded);
    return () => {
      socket.off("chatflowEnded", onChatflowEnded);
    };
  }, [endChatflow]);

  const user = useRecipient();
  const addSocketMessage = useStore(conversationsStore, (s) => s.addSocketMessage);
  useEffect(() => {
    if (!user) {
      return;
    }
    const onAgentMessageEvent: ServerToClientEvents["agentMessage"] = ({ data: message }, ack) => {
      ack({ tenantId: tenant, data: user });
      addSocketMessage(message.conversation_id, message);
    };
    socket.on("agentMessage", onAgentMessageEvent);
    return () => {
      socket.off("agentMessage", onAgentMessageEvent);
    };
  }, [addSocketMessage, tenant, user]);

  const setTicket = useStore(conversationsStore, (s) => s.setTicket);
  useEffect(() => {
    const onTicketCreated: ServerToClientEvents["ticketCreated"] = ({
      data: { conversationId, ticketId },
    }) => setTicket(conversationId, ticketId);

    socket.on("ticketCreated", onTicketCreated);
    return () => {
      socket.off("ticketCreated", onTicketCreated);
    };
  }, [setTicket]);

  return conversationsStore;
};

type ConversationsStore = StoreApi<ConversationsState>;
export const ConversationsContext = createContext<ConversationsStore | null>(null);

export function useConversationsContext<T>(
  selector: (state: ConversationsState) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T {
  const store = useContext(ConversationsContext);
  if (!store) throw new Error("Missing ConversationsContext.Provider");
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

export function useConversations() {
  return useConversationsContext((c) => c.conversations);
}

export function useUnreadCount(...conversationIds: string[]) {
  return useConversationsContext(
    useCallback((c) => c.getUnreadCount(...conversationIds), [conversationIds]),
  );
}

export function useUnreadCountExcluding(...conversationIds: string[]) {
  return useConversationsContext(
    useCallback((c) => c.getUnreadCountExcept(...conversationIds), [conversationIds]),
  );
}

export function useAddInboundMessage() {
  return useConversationsContext((c) => c.addInboundMessage);
}

export function useCreateConversation() {
  return useConversationsContext((c) => c.createConversation);
}

export function useSetMessageRead() {
  return useConversationsContext((c) => c.setReadMessage);
}

export function useSetConversationCompleted() {
  return useConversationsContext((c) => c.setConversationCompleted);
}

export const ConversationsProvider = ({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}) => {
  const store = useConversationsStore(tenantId);
  return <ConversationsContext.Provider value={store}>{children}</ConversationsContext.Provider>;
};
